import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import UIState from "../../store/UIState";
import Ai from "../../store/models/Ai";
import User from "../../store/models/User";
import { DeleteIcon, DocumentsIcon, OverviewIcon, EditIcon, VerticalDotsIcon } from "../Svg";
import { Button } from "../Button";
import Loader from "../Loader";
import { Pagination } from "../Pagination";
import { FaCheckCircle, FaTimesCircle, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { ModalTypes } from "../../constants";
import { DropdownMenu, DropdownMenuItem } from "../DropdownMenu";
import _ from "lodash";

export const KnowledgeTab = (props) => {
  const dispatch = useDispatch();
  const { chatbot, projectId, isMini = false } = props;
  const [error, setError] = useState();
  const [crtPage, setCrtPage] = useState(1);
  const [documents, setDocuments] = useState();
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [partnerUsers, setPartnerUsers] = useState([]);

  useEffect(() => {
    fetchData()
    fetchPartnerUsers()
  }, [chatbot.id, projectId])

  const fetchData = async (page = 0) => {
    try {
      setError()
      setLoading(true);
      let res = await dispatch(Ai.actions.fetchChatbotDocuments(chatbot.id, projectId, page));
      setDocuments(_.get(res, 'documents'));
      setTotalPages(_.get(res, 'total_pages'));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const fetchPartnerUsers = async () => {
    try {
      const users = await dispatch(User.actions.fetchOrganizationUsers());
      const partnersOnly = _.filter(users, user => user.role === 'partner' && !user.disabled);
      setPartnerUsers(partnersOnly);
    } catch (e) {
      console.error('Failed to fetch partner users:', e);
    }
  };

  const handlePageChange = pageNumber => {
    setCrtPage(pageNumber);
    fetchData(pageNumber - 1);
  }

  const handleAddDocument = () => {
    dispatch(UIState.actions.showModal(ModalTypes.CREATE_DOCUMENT, {
      chatbotId: chatbot.id,
      projectId: projectId,
      onSuccess: (document) => {
        setDocuments([document, ...documents]);
      }
    }));
  };

  const DocumentComponent = isMini ? DocumentMiniCard : DocumentCard;

  console.log('documents');
  console.log(documents);

  return <div className='AiServicePage__tab-content'>
    <div className='RagChatbotDetailPage knowledge-base-tab'>
      {loading && <div className={'EmptyState'}>
        <Loader size='large' color='grey' />
      </div>}
      {!loading && documents && documents.length > 0 && <>
        <div className='RagChatbotDetailPage__header'>
          <Button
            className="RagChatbotDetailPage__add-button"
            color="green-outline"
            onClick={handleAddDocument}
            icon={<DocumentsIcon />}
            title="New Document"
            size={isMini ? "medium" : "small"}
          />
        </div>
        <div className='RagChatbotDetailPage__documents'>
          {documents.map(doc => <DocumentComponent key={doc.id} doc={doc} fetchData={fetchData} crtPage={crtPage} projectId={projectId} chatbotId={chatbot.id} partnerUsers={partnerUsers} />)}
        </div>
        <div className='RagChatbotDetailPage__pagination'>
          <Pagination
            size={'lg'}
            currentPage={crtPage}
            totalPages={totalPages}
            onPageChange={handlePageChange} />
        </div>
      </>}
      {!loading && _.isEmpty(documents) && <div className={'RagChatbotDetailPage__empty-state'}>
        No Documents
        <div className='RagChatbotDetailPage__header'>
          <Button
            className="RagChatbotDetailPage__add-button"
            color="green-outline"
            onClick={handleAddDocument}
            icon={<DocumentsIcon />}
            title="Add First Document"
            size={isMini ? "medium" : "small"}
          />
        </div>
      </div>
      }
    </div>
  </div>
}

const DocumentCard = (props) => {
  const { doc, fetchData, crtPage, projectId, chatbotId, partnerUsers } = props;
  const type = _.get(doc, 'metadata.type');
  const dispatch = useDispatch();
  const [currentDoc, setCurrentDoc] = useState(doc);
  const [isMetadataVisible, setIsMetadataVisible] = useState(false);
  const [editedMetadataJson, setEditedMetadataJson] = useState(() => {
    const { type, filename, text, ...editableMetadata } = doc.metadata;
    return JSON.stringify(editableMetadata, null, 2);
  });
  const [originalMetadataJson, setOriginalMetadataJson] = useState(() => {
    const { type, filename, text, ...editableMetadata } = doc.metadata;
    return JSON.stringify(editableMetadata, null, 2);
  });
  const [jsonError, setJsonError] = useState('');
  const [isOpening, setIsOpening] = useState(false);
  const metadataTextareaRef = useRef(null);

  useEffect(() => {
    if (isMetadataVisible && metadataTextareaRef.current) {
      metadataTextareaRef.current.focus();
    }
  }, [isMetadataVisible]);

  const handleDelete = () => {
    const onConfirm = async () => {
      await dispatch(Ai.actions.deleteChatbotDocument(projectId, chatbotId, doc.id));
      await fetchData(crtPage - 1);
    }
    dispatch(UIState.actions.showModal(ModalTypes.DELETE, { title: doc.id, onConfirm }));
  }

  const handleOpen = async () => {
    dispatch(UIState.actions.showModal(ModalTypes.VIEW_DOCUMENT, {
      document: doc,
      projectId,
      chatbotId: chatbotId,
      onRequestClose: () => dispatch(UIState.actions.hideModal(ModalTypes.VIEW_DOCUMENT))
    }));
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let updatedMetadata;
      try {
        const editedMetadata = JSON.parse(editedMetadataJson);

        updatedMetadata = {
          ...editedMetadata,
          type: currentDoc.metadata.type,
          ...(currentDoc.metadata.filename && { filename: currentDoc.metadata.filename }),
          ...(currentDoc.metadata.text && { text: currentDoc.metadata.text })
        };

        setJsonError('');
      } catch (err) {
        setJsonError('Invalid JSON format');
        return;
      }

      await dispatch(Ai.actions.updateChatbotDocument(chatbotId, doc.id, {
        project_id: projectId,
        metadata: JSON.stringify(updatedMetadata)
      }));
      setCurrentDoc(d => ({ ...d, metadata: updatedMetadata }));
      setOriginalMetadataJson(editedMetadataJson);
      toast.success('Metadata updated successfully');
    } catch (e) {
      toast.error('Failed to update metadata')
      setEditedMetadataJson(originalMetadataJson);
    }
  }

  const handleCancel = () => {
    setEditedMetadataJson(originalMetadataJson);
    setJsonError('');
  }

  const hasChanges = editedMetadataJson !== originalMetadataJson;
  const lastUpdated = _.get(doc, 'ui.ts') ? new Date(_.get(doc, 'ui.ts')).toLocaleString() : '-';
  const uploadedBy = (doc?.ui?.user === 'null') ? 'External' : doc?.ui?.user ?? 'External';

  return (
    <div className='KnowledgeDocument' key={doc.id}>
      <div className='KnowledgeDocument__main'>
        <div className='KnowledgeDocument__info'>
          {type === 'file' && (
            <div className='KnowledgeDocument__property'>
              <span className='property-label'>File name:</span>
              <span className='property-value'>{_.get(doc, 'metadata.filename')}</span>
            </div>
          )}
          <div className='KnowledgeDocument__property'>
            <span className='property-label'>Document ID:</span>
            <span className='property-value'>{doc.id}</span>
          </div>
          <div className='KnowledgeDocument__property'>
            <span className='property-label'>Uploaded by:</span>
            <span className='property-value'>{uploadedBy}</span>
          </div>
          <div className='KnowledgeDocument__property'>
            <span className='property-label'>Last updated:</span>
            <span className='property-value'>{lastUpdated}</span>
          </div>

          {_.get(doc, 'metadata.description') && (
            <div className='KnowledgeDocument__property'>
              <span className='property-label'>Description:</span>
              <span className='property-value'>{_.get(doc, 'metadata.description')}</span>
            </div>
          )}
        </div>

        <div className='KnowledgeDocument__actions'>
          <Button
            className="KnowledgeDocument__button delete Button--icon-only"
            onClick={handleDelete}
            color="red-outline"
            size="small"
            icon={<DeleteIcon />}
          />
          <Button
            className="KnowledgeDocument__button"
            onClick={handleOpen}
            color="green-outline"
            title={isOpening ? "" : "Edit"}
            size="small"
            icon={isOpening ? <Loader size="small" color="green" /> : <OverviewIcon />}
            disabled={isOpening}
          >
            Open
          </Button>
        </div>
      </div>

      <div className='KnowledgeDocument__bottom-actions'>
        <Button
          className={`KnowledgeDocument__button KnowledgeDocument__metadata-toggle reverse ${isMetadataVisible ? 'active' : ''}`}
          onClick={() => setIsMetadataVisible(!isMetadataVisible)}
          color="grey-outline"
          size="small"
          icon={isMetadataVisible ? <FaChevronUp /> : <FaChevronDown />}
          title={"Edit Metadata"} />

        <PartnerDropdownMenu
          projectId={projectId}
          chatbotId={chatbotId}
          docId={doc.id}
          document={doc}
          partnerUsers={partnerUsers}
        />
      </div>

      {isMetadataVisible && (
        <div className='KnowledgeDocument__metadata'>
          <div className='KnowledgeDocument__metadata-content'>
            <textarea
              ref={metadataTextareaRef}
              value={editedMetadataJson}
              onChange={(e) => setEditedMetadataJson(e.target.value)}
              style={{ fontFamily: 'monospace' }}
            />
            {jsonError && (
              <div className='KnowledgeDocument__error'>
                {jsonError}
              </div>
            )}
            {hasChanges && (
              <div className='KnowledgeDocument__metadata-actions'>
                <div className='KnowledgeDocument__metadata-actions--icon cancel' onClick={handleCancel} title="Cancel"><FaTimesCircle /></div>
                <div className='KnowledgeDocument__metadata-actions--icon submit' onClick={handleSubmit} title="Submit"><FaCheckCircle /></div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DocumentMiniCard = (props) => {
  const { doc, fetchData, crtPage, projectId, chatbotId, partnerUsers } = props;
  const dispatch = useDispatch();
  const [currentDoc, setCurrentDoc] = useState(doc);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(_.get(doc, 'metadata.description', ''));
  const [originalDescription, setOriginalDescription] = useState(_.get(doc, 'metadata.description', ''));
  const [isOpening, setIsOpening] = useState(false);
  const descriptionInputRef = useRef(null);

  const handleDelete = () => {
    const onConfirm = async () => {
      await dispatch(Ai.actions.deleteChatbotDocument(projectId, chatbotId, doc.id));
      await fetchData(crtPage - 1);
    }
    dispatch(UIState.actions.showModal(ModalTypes.DELETE, { title: doc.id, onConfirm }));
  }

  useEffect(() => {
    if (isEditingDescription && descriptionInputRef.current) {
      descriptionInputRef.current.focus();
    }
  }, [isEditingDescription]);

  const handleOpen = async () => {
    dispatch(UIState.actions.showModal(ModalTypes.VIEW_DOCUMENT, {
      document: doc,
      projectId,
      chatbotId: chatbotId,
      onRequestClose: () => dispatch(UIState.actions.hideModal(ModalTypes.VIEW_DOCUMENT))
    }));
  }

  const handleDescriptionSubmit = async () => {
    try {
      const updatedMetadata = {
        ...currentDoc.metadata,
        description: editedDescription.trim()
      };

      await dispatch(Ai.actions.updateChatbotDocument(chatbotId, doc.id, {
        project_id: projectId,
        metadata: JSON.stringify(updatedMetadata)
      }));

      setCurrentDoc(d => ({ ...d, metadata: updatedMetadata }));
      setOriginalDescription(editedDescription);
      setIsEditingDescription(false);
      toast.success('Description updated successfully');
    } catch (e) {
      toast.error('Failed to update description');
      setEditedDescription(originalDescription);
    }
  }

  const handleDescriptionCancel = () => {
    setEditedDescription(originalDescription);
    setIsEditingDescription(false);
  }

  const hasDescriptionChanges = editedDescription !== originalDescription;
  const lastUpdated = _.get(doc, 'ui.ts') ? new Date(_.get(doc, 'ui.ts')).toLocaleString() : '-';
  const filename = _.get(doc, 'metadata.filename');
  const description = _.get(currentDoc, 'metadata.description', '');

  return (
    <div className='KnowledgeDocument KnowledgeDocument--mini' key={doc.id}>
      <div className='KnowledgeDocument__info'>
        {filename && (
          <div className='KnowledgeDocument__property'>
            <span className='property-label'>File name:</span>
            <span className='property-value'>{filename}</span>
          </div>
        )}

        <div className='KnowledgeDocument__property'>
          <span className='property-label'>Last updated:</span>
          <span className='property-value'>{lastUpdated}</span>
        </div>

        <div className='KnowledgeDocument__property KnowledgeDocument__property--description-stacked'>
          <span className='property-label'>Description:</span>
          <div className='property-value property-value--editable'>
            {isEditingDescription ? (
              <div className='KnowledgeDocument__description-edit'>
                <input
                  ref={descriptionInputRef}
                  type="text"
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleDescriptionSubmit();
                    } else if (e.key === 'Escape') {
                      handleDescriptionCancel();
                    }
                  }}
                  placeholder="Add a description..."
                />
                <div className='KnowledgeDocument__description-actions'>
                  <div className='KnowledgeDocument__description-actions--icon cancel' onClick={handleDescriptionCancel} title="Cancel">
                    <FaTimesCircle />
                  </div>
                  <div
                    className={`KnowledgeDocument__description-actions--icon submit ${!hasDescriptionChanges ? 'disabled' : ''}`}
                    onClick={hasDescriptionChanges ? handleDescriptionSubmit : undefined}
                    title="Submit"
                  >
                    <FaCheckCircle />
                  </div>
                </div>
              </div>
            ) : (
              <div className='KnowledgeDocument__description-display' onClick={() => setIsEditingDescription(true)}>
                {description || <span className='placeholder'>Click to add description...</span>}
                <EditIcon className='edit-icon' />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='KnowledgeDocument__actions--mini'>
        <Button
          className="KnowledgeDocument__button delete Button--icon-only"
          onClick={handleDelete}
          color="red-outline"
          size="medium"
          icon={<DeleteIcon />}
        />
        <Button
          className="KnowledgeDocument__button"
          onClick={handleOpen}
          color="green"
          title={isOpening ? "" : "Open"}
          size="medium"
          icon={isOpening && <Loader size="small" color="green" />}
          disabled={isOpening}
        >
          Open
        </Button>
      
      </div>
    </div>
  );
};

const PartnerDropdownMenu = ({ projectId, chatbotId, docId, document, partnerUsers, className = "" }) => {
  const dispatch = useDispatch();
  const [isAssigningPartner, setIsAssigningPartner] = useState(false);
  const [isAssignedToPartners, setIsAssignedToPartners] = useState(document?.for_partner || false);

  useEffect(() => {
    setIsAssignedToPartners(document?.for_partner || false);
  }, [document?.for_partner]);

  const handleTogglePartnerAssignment = async () => {
    setIsAssigningPartner(true);
    try {
      if (isAssignedToPartners) {
        await dispatch(User.actions.removeKbDocFromPartner(chatbotId, docId, projectId));
        setIsAssignedToPartners(false);
        toast.success('Document removed from partners');
      } else {
        await dispatch(User.actions.assignKbDocToPartner(chatbotId, docId, projectId));
        setIsAssignedToPartners(true);
        toast.success('Document assigned to partners');
      }
    } catch (e) {
      toast.error(`Failed to ${isAssignedToPartners ? 'remove' : 'assign'} document: ${e.message}`);
    }
    setIsAssigningPartner(false);
  };

  if (partnerUsers.length === 0) {
    return null;
  }

  const menuLabel = isAssignedToPartners ? "Remove from partners" : "Assign to partners";

  return (
    <DropdownMenu
      icon={<VerticalDotsIcon />}
      className={`KnowledgeDocument__partner-menu ${className}`}
    >
      <DropdownMenuItem
        label={menuLabel}
        disabled={isAssigningPartner}
        onClick={handleTogglePartnerAssignment}
      />
    </DropdownMenu>
  );
};